<template>
  <span v-if="loaded">
    <el-select
      v-if="!useVuetify"
      v-model="selectedId"
      @change="do_change"
      :placeholder="placeholder"
      :size="size"
      :filterable="true"
      :multiple="multi"
    >
      <el-option
        v-if="allowAll && !multi"
        :value="0"
        :label="'Any Status'"
      ></el-option>
      <el-option
        v-for="option in leadStatusesByLeadType"
        :value="option.id"
        :key="option.id"
        :label="option.label"
      >
        <!-- TODO add label color -->
      </el-option>
      <!-- remove this option once all invalid lead statuses are remove form db -->
      <el-option
        v-if="!multi && selectedId && !isSelectedIdValid"
        :value="selectedId"
        :label="'Unknown Status'"
      ></el-option>
    </el-select>
    <v-select
      v-else
      v-model="selectedId"
      @update:model-value="do_change"
      :label="label"
      :placeholder="placeholder"
      :density="density"
      :multiple="multi"
      :items="selectOptions"
      :error="error"
      :error-messages="errorMessages"
      hide-details="auto"
      variant="outlined"
    ></v-select>
  </span>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'leadStatusSelect',
  emits: ['update:leadStatus', 'update:modelValue'],
  computed: {
    ...mapGetters('leadStatus', ['getLeadStatuses']),
    ...mapGetters('leadType', ['getLeadTypes']),
    leadStatusesByLeadType() {
      if (!this.selectedLeadType) {
        // return all possible statuses if no lead type set; will be 0 or undefined
        return this.getLeadStatuses.toSorted((a, b) => a.label - b.label);
      }
      const leadType = this.getLeadTypes.find(
        (lt) => lt.id == this.selectedLeadType
      );

      // if no lead type is found, return all statuses or an empty array
      if (!leadType || !leadType.leadStatuses) {
        console.warn(
          `Lead type with ID ${this.selectedLeadType} not found or has no statuses`
        );
        return this.getLeadStatuses.toSorted((a, b) => a.label - b.label);
      }

      const leadStatuses = [];
      leadType.leadStatuses
        .toSorted((a, b) => a.display_order - b.display_order)
        .forEach((ls) => {
          const status = this.getLeadStatuses.find(
            (s) => s.id == ls.lead_status_id
          );
          if (status) {
            leadStatuses.push({
              ...status,
              pipeline_order: ls.pipeline_order,
              display_order: ls.display_order,
            });
          }
        });
      return leadStatuses;
    },
    isSelectedIdValid() {
      if (this.selectedId == undefined) {
        return false;
      }
      // For multi we would need to inject unknown for each status that was missing
      // But we only add one option in the select so just work with single for now
      return this.leadStatusesByLeadType.some(
        (status) => status.id === this.selectedId
      );
    },
    selectOptions() {
      const options = this.leadStatusesByLeadType.map((status) => ({
        value: status.id,
        title: status.label,
      }));

      if (this.allowAll && !this.multi) {
        options.unshift({ value: 0, title: 'Any Status' });
      }

      // Add unknown status option if needed
      if (!this.multi && this.selectedId && !this.isSelectedIdValid) {
        options.push({ value: this.selectedId, title: 'Unknown Status' });
      }

      return options;
    },
  },
  props: {
    initialId: {
      type: [Number, Array],
      required: false,
    },
    size: {
      type: String,
      default: () => {
        return 'small';
      },
    },
    placeholder: {
      type: String,
      required: false,
      default: () => {
        return 'Select a Lead Status';
      },
    },
    label: {
      type: String,
      required: false,
      default: () => {
        return 'Lead Status';
      },
    },
    allowAll: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    multi: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    selectedLeadType: {
      type: Number,
      required: true,
      // Setting a value of 0 means show all statuses
    },
    useVuetify: {
      type: Boolean,
      required: false,
      default: true,
    },
    density: {
      type: String,
      required: false,
      default: 'compact',
    },
    error: {
      type: Boolean,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loaded: false,
      selectedId: undefined,
    };
  },
  watch: {
    initialId(id) {
      this.selectedId = id;
    },
  },
  async mounted() {
    if (!this.getLeadStatuses || this.getLeadStatuses.length == 0) {
      await this.fetchLeadStatuses();
    }
    // Will be array or scalar
    if (this.initialId) {
      this.selectedId = this.initialId;
    }
    this.loaded = true;
  },
  methods: {
    ...mapActions('leadStatus', ['fetchLeadStatuses']),
    do_change() {
      // Multi will always be an array or undefined
      if (this.multi) {
        this.$emit('update:leadStatus', this.selectedId);
        return;
      }
      // Single varies based on valid values
      const leadStatus = this.getLeadStatuses.find(
        (s) => s.id == this.selectedId
      );
      if (leadStatus) {
        this.$emit('update:modelValue', leadStatus.id);
        this.$emit('update:leadStatus', leadStatus);
      } else {
        // Because we parse from a string at times 0 instead of undefined
        this.$emit('update:modelValue', 0);
        this.$emit('update:leadStatus', undefined);
      }
    },
  },
};
</script>
